import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface SystemMetrics {
  cpu: number
  memory: number
  activeConnections: number
  requestsPerMinute: number
  averageResponseTime: number
  errorRate: number
}

interface Alert {
  id: string
  type: 'error' | 'warning' | 'info'
  title: string
  message: string
  timestamp: number
  resolved: boolean
}

interface MonitoringState {
  metrics: SystemMetrics
  alerts: Alert[]
  isLive: boolean
  lastUpdate: number | null
}

const initialState: MonitoringState = {
  metrics: {
    cpu: 0,
    memory: 0,
    activeConnections: 0,
    requestsPerMinute: 0,
    averageResponseTime: 0,
    errorRate: 0,
  },
  alerts: [],
  isLive: false,
  lastUpdate: null,
}

const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState,
  reducers: {
    updateMetrics: (state, action: PayloadAction<Partial<SystemMetrics>>) => {
      state.metrics = { ...state.metrics, ...action.payload }
      state.lastUpdate = Date.now()
    },
    addAlert: (state, action: PayloadAction<Omit<Alert, 'id' | 'timestamp' | 'resolved'>>) => {
      const alert: Alert = {
        ...action.payload,
        id: `alert-${Date.now()}`,
        timestamp: Date.now(),
        resolved: false,
      }
      state.alerts.unshift(alert)
      // Keep only last 100 alerts
      if (state.alerts.length > 100) {
        state.alerts = state.alerts.slice(0, 100)
      }
    },
    resolveAlert: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find(a => a.id === action.payload)
      if (alert) {
        alert.resolved = true
      }
    },
    clearAlerts: (state) => {
      state.alerts = []
    },
    setLiveStatus: (state, action: PayloadAction<boolean>) => {
      state.isLive = action.payload
    },
    resetMetrics: (state) => {
      state.metrics = initialState.metrics
      state.lastUpdate = null
    },
  },
})

export const {
  updateMetrics,
  addAlert,
  resolveAlert,
  clearAlerts,
  setLiveStatus,
  resetMetrics,
} = monitoringSlice.actions

export default monitoringSlice.reducer